/**
 * "Request data-room access to view sources" — shown on the investor score
 * screen when the score arrived WITHOUT its proof material (owner rule,
 * 2026-10-19: "the data room is a pass to access all claims and proof points").
 *
 * This is NOT a new request flow. It drives the existing one —
 * `useRequestDataRoomAccess` (request row first, then the deal's stage, the
 * same hook startup-detail's single "Request Access" path uses) — and reads the
 * investor's own request rows (`useMyDataRoomAccessRequests` + `requestStateFor`)
 * so it never re-offers a request that already exists.
 *
 * `purpose` only changes the sentence (request-access-copy.ts): the same pass
 * also opens a due diligence and its report (owner rule, 2026-10-23), so the DD
 * screens reuse this path instead of showing an error.
 */
import React, { useState } from 'react';
import { View, Text, Pressable } from 'react-native';
import { Lock } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useAuth } from '@/lib/supabase/auth';
import { useMyDataRoomAccessRequests } from '@/lib/supabase/api';
import { useRequestDataRoomAccess } from '@/lib/dealflow/deal-actions';
import { requestStateFor } from '@/lib/dd/dd-entry';
import { useAlert } from '@/components/AlertDialog';
import { handleError } from '@/lib/errors';
import { requestAccessCopy, type RequestAccessPurpose } from './request-access-copy';

interface Props {
  /** startup_profiles.id — whose data room is requested. */
  startupId: string;
  /** dealflow.id when the deal is in the pipeline; null for a raising-feed item. */
  dealId: string | null;
  startupName: string;
  /** What the pass is being asked for. Default: the score's sources. */
  purpose?: RequestAccessPurpose;
}

export function RequestSourcesAccessCta({ startupId, dealId, startupName, purpose = 'sources' }: Props) {
  const colors = useThemeColors();
  const investorProfile = useProfileStore((s) => s.investorProfile);
  const { user } = useAuth();
  const { data: myRequests = [] } = useMyDataRoomAccessRequests(investorProfile?.id);
  const { requestDataRoomAccess, isRequesting } = useRequestDataRoomAccess();
  const { error, success, confirm } = useAlert();
  const [sent, setSent] = useState(false);

  const state = sent ? 'pending' : requestStateFor(startupId, myRequests);

  const { message, button } = requestAccessCopy(purpose, state);

  const onRequest = async () => {
    if (!investorProfile?.id) {
      error('Cannot Request Access', 'Your profile is still loading. Please try again in a moment.');
      return;
    }
    if (!(await confirm('Request Data Room Access', `Send a request to ${startupName} for access to their data room?`))) {
      return;
    }
    try {
      await requestDataRoomAccess({
        dealId,
        startupId,
        investorProfileId: investorProfile.id,
        investorUserId: user?.id,
      });
      setSent(true);
      success('Request Sent', 'The founder will be asked to open their data room.');
    } catch (err) {
      const { message: msg, isCancelled } = handleError(err, { context: 'RequestSourcesAccessCta.onRequest' });
      if (!isCancelled) error('Request Failed', msg);
    }
  };

  return (
    <View
      className="rounded-xl p-3 mt-3 overflow-hidden"
      style={{ backgroundColor: colors.surface.primary, borderWidth: 1, borderColor: colors.border.light }}
    >
      <View className="flex-row items-start gap-2">
        <Lock size={14} color={colors.text.tertiary} style={{ marginTop: 2 }} />
        <Text className="text-xs leading-4 flex-1" style={{ color: colors.text.secondary }} numberOfLines={3}>
          {message}
        </Text>
      </View>
      {state === 'none' ? (
        <View
          style={{ height: 40, borderRadius: 10, marginTop: 10, overflow: 'hidden', backgroundColor: colors.primary[600], opacity: isRequesting ? 0.6 : 1 }}
        >
          <Pressable
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            onPress={() => void onRequest()}
            disabled={isRequesting}
            accessibilityRole="button"
            accessibilityLabel={button}
          >
            <Text className="text-white text-sm font-semibold" numberOfLines={1}>
              {button}
            </Text>
          </Pressable>
        </View>
      ) : null}
    </View>
  );
}
